import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListLabourQuery,
  useAddUpdateLabourMutation,
  useDeleteLabourMutation,
} from '../api/enhanceGenerated';
import { useModuleAccess } from '../hooks/useModuleAccess';

type LabourRow = { id: string; name: string; phone?: string };

function useLabourList(args: ResourceListArgs) {
  const result = useListLabourQuery(args);
  const data: PaginatedResult<LabourRow> | undefined = result.data?.listLabour
    ? {
        ...result.data.listLabour,
        rows: result.data.listLabour.rows.map((r) => ({
          id: r.id,
          name: r.name,
          phone: r.phone ?? undefined,
        })),
      }
    : undefined;
  return { ...result, data };
}

export function LabourPage() {
  const { t } = useTranslation();
  const access = useModuleAccess('labour');
  const [save, { isLoading: saving }] = useAddUpdateLabourMutation();
  const [remove] = useDeleteLabourMutation();

  return (
    <ResourceCrudPage<LabourRow>
      title={t('nav.labour')}
      description={t('labour.description')}
      entityLabel={t('labour.entity')}
      entityLabelPlural={t('labour.entityPlural')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useLabourList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        { key: 'phone', label: t('common.phone'), type: 'phone' },
      ]}
      onSave={(values, editing) =>
        save({ input: { ...values, id: editing?.id } }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}
